import { MessageSquareText } from 'lucide-react'
import { memo, type PointerEvent as RPointerEvent } from 'react'
import { BASELINE_EM, cssFont, TEXT_PAD } from '@/lib/fonts'
import { NOTE_SIZE } from '@/lib/geometry'
import { arrowHead, inkPath } from '@/lib/ink'
import type { Annotation, FreeTextAnn, TextEditAnn } from '@/types'

type Down = (e: RPointerEvent, a: Annotation) => void

/** Highlights and highlighter-pen strokes, drawn in a multiply-blended layer so text stays crisp. */
export const MarkupShape = memo(function MarkupShape({ a }: { a: Annotation }) {
  if (a.type === 'highlight')
    return (
      <g fill={a.color} opacity={a.opacity}>
        {a.rects.map((r, i) => (
          <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} rx={Math.min(1.5, r.h * 0.1)} />
        ))}
      </g>
    )
  if (a.type === 'ink' && a.marker) return <path d={inkPath(a)} fill={a.color} opacity={a.opacity} />
  return null
})

export const isMarkup = (a: Annotation) => a.type === 'highlight' || (a.type === 'ink' && Boolean(a.marker))

/** Everything else that lives in SVG. `interactive` adds generous invisible hit targets. */
export const SvgShape = memo(function SvgShape({
  a,
  interactive,
  onDown,
}: {
  a: Annotation
  interactive: boolean
  onDown?: Down
}) {
  const hit = interactive
    ? {
        onPointerDown: (e: RPointerEvent) => onDown?.(e, a),
        style: { pointerEvents: 'auto' as const, cursor: 'move' },
        'data-ann': a.id,
      }
    : {}
  const hitStroke = 'transparent'
  switch (a.type) {
    case 'highlight':
      return interactive ? (
        <g {...hit}>
          {a.rects.map((r, i) => (
            <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} fill="transparent" />
          ))}
        </g>
      ) : null
    case 'underline':
    case 'strikeout':
      return (
        <g {...hit}>
          {a.rects.map((r, i) => {
            const t = Math.max(0.75, r.h * 0.075)
            const y = a.type === 'underline' ? r.y + r.h - t / 2 : r.y + r.h * 0.55
            return (
              <g key={i}>
                {interactive && <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="transparent" />}
                <line
                  x1={r.x}
                  x2={r.x + r.w}
                  y1={y}
                  y2={y}
                  stroke={a.color}
                  strokeWidth={t}
                  opacity={a.opacity}
                />
              </g>
            )
          })}
        </g>
      )
    case 'ink': {
      if (a.marker)
        return interactive ? (
          <path {...hit} d={inkPath(a)} fill="transparent" stroke={hitStroke} strokeWidth={8} />
        ) : null
      const d = inkPath(a)
      return (
        <g {...hit}>
          {interactive && (
            <path d={d} fill="none" stroke={hitStroke} strokeWidth={10} strokeLinejoin="round" />
          )}
          <path d={d} fill={a.color} opacity={a.opacity} />
        </g>
      )
    }
    case 'rect':
      return (
        <g {...hit}>
          <rect
            x={a.x}
            y={a.y}
            width={a.w}
            height={a.h}
            fill={a.fill ?? 'none'}
            stroke={a.color}
            strokeWidth={a.strokeWidth}
            opacity={a.opacity}
          />
          {interactive && !a.fill && (
            <rect
              x={a.x}
              y={a.y}
              width={a.w}
              height={a.h}
              fill="none"
              stroke={hitStroke}
              strokeWidth={a.strokeWidth + 10}
            />
          )}
        </g>
      )
    case 'ellipse':
      return (
        <g {...hit}>
          <ellipse
            cx={a.x + a.w / 2}
            cy={a.y + a.h / 2}
            rx={a.w / 2}
            ry={a.h / 2}
            fill={a.fill ?? 'none'}
            stroke={a.color}
            strokeWidth={a.strokeWidth}
            opacity={a.opacity}
          />
          {interactive && !a.fill && (
            <ellipse
              cx={a.x + a.w / 2}
              cy={a.y + a.h / 2}
              rx={a.w / 2}
              ry={a.h / 2}
              fill="none"
              stroke={hitStroke}
              strokeWidth={a.strokeWidth + 10}
            />
          )}
        </g>
      )
    case 'line':
    case 'arrow': {
      const head = a.type === 'arrow' ? arrowHead(a) : null
      const end = head?.shaftEnd ?? { x: a.x2, y: a.y2 }
      return (
        <g {...hit} opacity={a.opacity}>
          {interactive && (
            <line
              x1={a.x1}
              y1={a.y1}
              x2={a.x2}
              y2={a.y2}
              stroke={hitStroke}
              strokeWidth={a.strokeWidth + 12}
            />
          )}
          <line
            x1={a.x1}
            y1={a.y1}
            x2={end.x}
            y2={end.y}
            stroke={a.color}
            strokeWidth={a.strokeWidth}
            strokeLinecap="round"
          />
          {head && (
            <path
              d={head.path}
              fill={a.color}
              stroke={a.color}
              strokeWidth={a.strokeWidth * 0.5}
              strokeLinejoin="round"
            />
          )}
        </g>
      )
    }
    case 'image':
      return (
        <g {...hit}>
          <image
            href={a.src}
            x={a.x}
            y={a.y}
            width={a.w}
            height={a.h}
            opacity={a.opacity}
            preserveAspectRatio="none"
          />
          {interactive && <rect x={a.x} y={a.y} width={a.w} height={a.h} fill="transparent" />}
        </g>
      )
    default:
      return null
  }
})

export function FreeTextView({
  a,
  interactive,
  onDown,
}: {
  a: FreeTextAnn
  interactive: boolean
  onDown?: Down
}) {
  return (
    <div
      data-ann={a.id}
      onPointerDown={interactive ? (e) => onDown?.(e, a) : undefined}
      className="absolute whitespace-pre-wrap break-words"
      style={{
        left: a.x,
        top: a.y,
        width: a.w,
        minHeight: a.h,
        padding: TEXT_PAD,
        color: a.color,
        opacity: a.opacity,
        background: a.background ?? undefined,
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: interactive ? 'move' : undefined,
        overflowWrap: 'anywhere',
        ...cssFont(a),
      }}
    >
      {a.text || '​'}
    </div>
  )
}

export function TextEditView({
  a,
  interactive,
  onDown,
}: {
  a: TextEditAnn
  interactive: boolean
  onDown?: Down
}) {
  const lineTop = a.baseline - a.fontSize * BASELINE_EM[a.fontFamily]
  return (
    <div
      data-ann={a.id}
      onPointerDown={interactive ? (e) => onDown?.(e, a) : undefined}
      className="absolute"
      style={{
        left: a.x,
        top: a.y,
        width: a.w,
        height: a.h,
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: interactive ? 'text' : undefined,
      }}
    >
      <div className="absolute" style={{ inset: -1, background: a.cover }} />
      <div
        className="absolute whitespace-pre"
        style={{ left: 0, top: lineTop, color: a.color, opacity: a.opacity, ...cssFont(a) }}
      >
        {a.text}
      </div>
    </div>
  )
}

export function NoteIcon({
  a,
  interactive,
  onDown,
  selected,
}: {
  a: Extract<Annotation, { type: 'note' }>
  interactive: boolean
  onDown?: Down
  selected: boolean
}) {
  return (
    <div
      data-ann={a.id}
      onPointerDown={interactive ? (e) => onDown?.(e, a) : undefined}
      className="absolute flex items-center justify-center rounded-[5px] shadow-md transition-transform"
      style={{
        left: a.x,
        top: a.y,
        width: NOTE_SIZE,
        height: NOTE_SIZE,
        background: a.color,
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: interactive ? 'pointer' : undefined,
        outline: selected ? '1.5px solid var(--accent)' : undefined,
        outlineOffset: 1.5,
      }}
      title={a.text}
    >
      <MessageSquareText
        style={{ width: NOTE_SIZE * 0.62, height: NOTE_SIZE * 0.62 }}
        strokeWidth={2.2}
        color="#1f2937"
      />
    </div>
  )
}
